import { useState } from 'react';
import type { QuestionDetail, QuestionComment } from '@shared-ui/questions-page';
import { questionApi } from '../../api/questionApi';

const ROLE_COLOR: Record<string, string> = {
  admin: '#7c3aed',
  teacher: '#2563eb',
  student: '#059669',
};

/** Reviewer/SME comments panel for a question. Any authenticated user can post;
 *  commentBy / commentByType are derived server-side from the token. Mounted into
 *  the shared question detail view via the `renderDetailExtra` slot. */
export function QuestionComments({ question }: { question: QuestionDetail }) {
  const [comments, setComments] = useState<QuestionComment[]>((question.comments as QuestionComment[]) ?? []);
  const [text, setText] = useState('');
  const [type, setType] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Comments are read-only by default; the input controls appear only when the
  // reviewer clicks "Add comment".
  const [adding, setAdding] = useState(false);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await questionApi.addComment(question.id, {
        commentText: trimmed,
        ...(type.trim() ? { commentType: type.trim() } : {}),
      });
      setComments(updated as QuestionComment[]);
      setText('');
      setType('');
      setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add comment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="view-section">
      <h3>
        Comments
        {comments.length > 0 && (
          <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-tertiary)', fontWeight: 400 }}>
            ({comments.length})
          </span>
        )}
      </h3>

      {comments.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: '4px 0 8px' }}>No comments yet.</p>
      )}

      {comments.map((c, idx) => (
        <div key={idx} style={{ border: '1px solid var(--border-color)', background: 'var(--surface)', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4 }}>
            <strong style={{ color: 'var(--text-primary)' }}>{c.commentBy}</strong>
            <span style={{ color: ROLE_COLOR[c.commentByType] ?? 'var(--text-tertiary)', fontWeight: 600 }}>{c.commentByType}</span>
            {c.commentType && (
              <span style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', borderRadius: 4, padding: '1px 6px' }}>{c.commentType}</span>
            )}
            <span style={{ marginLeft: 'auto' }}>{new Date(c.commentOn).toLocaleString()}</span>
          </div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>{c.commentText}</div>
        </div>
      ))}

      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          style={{ marginTop: 4, padding: 0, background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          + Add comment
        </button>
      ) : (
        <div style={{ marginTop: 8 }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Add a comment…"
            rows={2}
            autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: 8, fontSize: 14, color: 'var(--text-primary)', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 6, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
            <input
              value={type}
              onChange={e => setType(e.target.value)}
              placeholder="type (optional)"
              style={{ width: 160, padding: '6px 8px', fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 6 }}
            />
            <button
              onClick={submit}
              disabled={busy || !text.trim()}
              style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600, color: busy || !text.trim() ? 'var(--text-tertiary)' : '#fff', background: busy || !text.trim() ? 'var(--bg-tertiary)' : 'var(--color-primary)', border: 'none', borderRadius: 6, cursor: busy || !text.trim() ? 'default' : 'pointer' }}
            >
              {busy ? 'Adding…' : 'Add comment'}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setText(''); setType(''); setError(null); }}
              disabled={busy}
              style={{ padding: '6px 10px', fontSize: 13, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer' }}
            >
              Cancel
            </button>
            {error && <span style={{ color: 'var(--color-error)', fontSize: 12 }}>{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
